"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { construireCalendrier, telechargerCalendrier } from "@/helpers/icsCalendar";
import { echeancesSociete } from "@/lib/tools/companyDeadlines";
import {
    Avancer,
    Champ,
    Ecran,
    Liste,
    Question,
    Recommencer,
    Resultat,
    Segments,
    dateLisible,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Les échéances d'une société, au jour près, sur les douze mois qui viennent.
 *
 * TOUS LES SITES PUBLIENT LE MÊME CALENDRIER GÉNÉRIQUE, QUE PERSONNE NE LIT.
 * « Acomptes les 15 mars, juin, septembre et décembre » ne dit rien à celui
 * dont l'impôt ne dépasse pas 3 000 € : il n'en doit aucun. Six questions
 * suffisent à trier ce qui le concerne, et la liste repart dans son agenda.
 *
 * Les règles et leurs limites sont dans lib/tools/companyDeadlines.js ;
 * npm run check:company-deadlines vérifie les dates contre le calendrier
 * fiscal 2026.
 */

const PHASES = {
    forme: 0,
    ir: 0,
    cloture: 0,
    premier: 1,
    creation: 1,
    is: 1,
    tva: 1,
    social: 2,
    dividendes: 2,
    resultat: 3,
};

const ICONES = {
    liasse: "document-text-linear",
    solde: "safe-square-linear",
    approbation: "pen-new-square-linear",
    depot: "clipboard-check-linear",
    dividendesLimite: "pie-chart-2-linear",
    acompteIs: "safe-square-linear",
    cfe: "buildings-2-linear",
    cfeDeclaration: "buildings-2-linear",
    tvaAcompteDecembre: "tag-price-linear",
    ifu: "document-text-linear",
    dispense: "user-check-linear",
};

const L = {
    fr: {
        phases: ["Société", "Impôts", "Vous"],
        retour: "Retour",
        forme: {
            titre: "Quelle est votre société ?",
            aide: "Ce calendrier couvre les sociétés à l'impôt sur les sociétés, avec un seul associé.",
            choix: [
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL à l'impôt sur les sociétés", detail: "Vous êtes gérant, et vous cotisez comme indépendant." },
                { cle: "sasu", icone: "user-check-linear", titre: "SASU", detail: "Vous êtes président, assimilé salarié." },
                { cle: "ir", icone: "case-minimalistic-linear", titre: "EURL à l'impôt sur le revenu, ou entreprise individuelle", detail: "Votre bénéfice est imposé à votre nom." },
            ],
        },
        ir: {
            titre: "Ce calendrier ne couvre pas encore votre situation",
            aide: "À l'impôt sur le revenu, vous n'avez ni acompte d'impôt sur les sociétés, ni liasse 2065 : vos échéances sont d'autres dates, que nous n'avons pas encore relevées.",
            points: [
                "Les grandes restent la CFE au 15 décembre et votre déclaration de revenus au printemps.",
                "Si vous hésitez à passer à l'impôt sur les sociétés, regardez d'abord ce que cela changerait pour vous.",
            ],
            lien: "Passer à l'impôt sur les sociétés",
        },
        cloture: {
            titre: "Quand se termine votre exercice en cours ?",
            aide: "C'est la date de clôture de vos statuts. Pour la plupart des sociétés, le 31 décembre.",
            trenteEtUn: "Le 31 décembre {annee}",
            libelle: "Date de clôture de l'exercice",
            autre: "À une autre date",
            date: "Date de clôture de l'exercice en cours",
            dateAide: "Une date à venir, dans les deux prochaines années.",
            erreur: "Choisissez une date à venir, dans les deux prochaines années.",
            suivant: "Continuer",
        },
        premier: {
            titre: "Votre société a-t-elle déjà clos un exercice ?",
            aide: "Pendant le premier exercice, plusieurs échéances n'existent pas encore.",
            choix: [
                { cle: "non", icone: "confetti-minimalistic-linear", titre: "Non, c'est notre premier exercice", detail: "La société a été créée il y a peu." },
                { cle: "oui", icone: "calendar-mark-linear", titre: "Oui, au moins un", detail: "Nous avons déjà déposé une liasse fiscale." },
            ],
        },
        creation: {
            titre: "Quand votre société a-t-elle commencé son activité ?",
            aide: "La date de début d'activité de votre Kbis. Votre première CFE en dépend.",
            date: "Début d'activité",
            suivant: "Continuer",
        },
        is: {
            titre: "L'impôt sur les sociétés de votre dernier exercice dépassait-il 3 000 € ?",
            aide: "En dessous, vous payez tout l'impôt d'un coup, au solde. 3 000 € d'impôt, c'est à peu près 20 000 € de bénéfice.",
            choix: [
                { cle: "plus", icone: "graph-up-linear", titre: "Oui, plus de 3 000 €", detail: "Vous payez quatre acomptes par an." },
                { cle: "moins", icone: "graph-down-linear", titre: "Non, 3 000 € ou moins", detail: "Tout se paie au solde." },
                { cle: "inconnu", icone: "question-circle-linear", titre: "Je ne sais pas", detail: "Nous afficherons les acomptes, avec cette réserve." },
            ],
        },
        tva: {
            titre: "Comment déclarez-vous la TVA ?",
            aide: "Votre expert-comptable ou votre espace professionnel sur impots.gouv.fr vous le dit.",
            choix: [
                { cle: "franchise", icone: "tag-price-linear", titre: "Je ne facture pas la TVA", detail: "Franchise en base : aucune déclaration." },
                { cle: "simplifie", icone: "calendar-linear", titre: "Deux acomptes et une déclaration par an", detail: "Le régime simplifié." },
                { cle: "normal", icone: "calendar-mark-linear", titre: "Une déclaration chaque mois", detail: "Le régime normal." },
            ],
        },
        social: {
            titreSasu: "Vous versez-vous un salaire ?",
            aideSasu: "Même petit : dès le premier salaire, une déclaration part chaque mois.",
            choixSasu: [
                { cle: "oui", icone: "wallet-money-linear", titre: "Oui", detail: "Je me verse un salaire de président." },
                { cle: "non", icone: "pie-chart-2-linear", titre: "Non", detail: "Je ne me paie qu'en dividendes, ou pas du tout." },
            ],
            titreEurl: "Quand vos cotisations sont-elles prélevées ?",
            aideEurl: "Votre échéancier sur urssaf.fr l'indique. Sans choix de votre part, c'est le 5 du mois.",
            choixEurl: [
                { cle: "5", icone: "calendar-linear", titre: "Le 5 de chaque mois", detail: "" },
                { cle: "20", icone: "calendar-linear", titre: "Le 20 de chaque mois", detail: "" },
                { cle: "trimestre", icone: "calendar-mark-linear", titre: "Chaque trimestre", detail: "Les 5 février, mai, août et novembre." },
            ],
        },
        dividendes: {
            titre: "Pensez-vous vous verser des dividendes dans l'année ?",
            aide: "Ils ajoutent trois échéances à votre calendrier.",
            choix: [
                { cle: "oui", icone: "pie-chart-2-linear", titre: "Oui", detail: "" },
                { cle: "non", icone: "close-circle-linear", titre: "Non", detail: "" },
            ],
        },
        resultat: {
            titre: "Vos échéances",
            entete: "Vos {n} prochaines dates",
            enteteUne: "Votre prochaine date",
            aucune: "Aucune échéance datée dans les douze prochains mois",
            jours: "dans {n} jours",
            demain: "demain",
            aujourdhui: "aujourd'hui",
            calculee: "Date calculée selon la règle : le calendrier fiscal 2027 n'est pas encore paru.",
            ponctuelles: {
                liasse: {
                    titre: "Liasse fiscale de l'exercice clos le {cloture}",
                    detail: "Votre déclaration de résultat et ses annexes, en ligne. La date légale est le {legale} ; en ligne, vous avez quinze jours de plus.",
                },
                solde: {
                    titre: "Solde de l'impôt sur les sociétés",
                    detail: "Pour l'exercice clos le {cloture} : l'impôt de l'année, moins les acomptes déjà versés. Si vos acomptes dépassent l'impôt, l'excédent vous est remboursé.",
                },
                approbation: {
                    titre: "Approbation des comptes",
                    detail: "Six mois après la clôture du {cloture}. Vous approuvez les comptes et décidez quoi faire du bénéfice.",
                },
                depot: {
                    titre: "Dépôt des comptes au greffe",
                    detail: "Un mois après votre décision, deux mois en ligne. La date affichée suppose une décision prise au dernier jour permis.",
                },
                dividendesLimite: {
                    titre: "Dernier jour pour verser vos dividendes",
                    detail: "Neuf mois après la clôture du {cloture}.",
                },
                acompteIs: {
                    titre: "Acompte d'impôt sur les sociétés",
                    detail: "Un quart de l'impôt de votre dernier exercice.",
                    incertain: "Seulement si cet impôt dépassait 3 000 €.",
                },
                cfe: {
                    titre: "Cotisation foncière des entreprises",
                    detail: "Le solde de votre CFE, à payer en ligne.",
                },
                cfeDeclaration: {
                    titre: "Déclaration initiale de CFE",
                    detail: "L'année de création, vous ne payez pas de CFE, mais le formulaire 1447-C est à déposer avant la fin de l'année.",
                },
                tvaAcompteDecembre: {
                    titre: "Acompte de TVA de décembre",
                    detail: "40 % de la TVA de l'an dernier, entre le 15 et le 24 décembre, à la date de votre espace professionnel. Vous en êtes dispensé si cette TVA était inférieure à 1 000 €.",
                },
                ifu: {
                    titre: "Déclaration des dividendes versés",
                    detail: "Votre société déclare en ligne les dividendes versés l'année d'avant, sur l'imprimé fiscal unique, et vous en envoie une copie.",
                },
                dispense: {
                    titre: "Dispense d'acompte sur vos dividendes",
                    detail: "Pour les dividendes de l'an prochain : si votre revenu fiscal de référence est sous 50 000 €, 75 000 € en couple, remettez à votre société une attestation sur l'honneur.",
                },
            },
            recurrentesTitre: "Ce qui revient toute l'année",
            recurrentes: {
                dsn: "Chaque 15 du mois : la déclaration sociale de votre salaire, et ses cotisations. Même un mois sans salaire, la déclaration reste due une fois le premier versé.",
                cotisationsMois: "Chaque {jour} du mois : vos cotisations Urssaf, calculées sur vos revenus passés puis ajustées une fois vos revenus déclarés.",
                cotisationsTrimestre: "Les 5 février, 5 mai, 5 août et 5 novembre : vos cotisations Urssaf, calculées sur vos revenus passés puis ajustées une fois vos revenus déclarés.",
                ca3: "Chaque mois, entre le 15 et le 24 : votre déclaration de TVA. Pour une société, c'est en général le 19, le 20 ou le 21, selon votre département : la date exacte est dans votre espace professionnel.",
                formulaire2777: "Le 15 du mois qui suit chaque versement de dividendes : le formulaire 2777, qui déclare et reverse l'acompte d'impôt de 12,8 % et les prélèvements sociaux.",
            },
            informationsTitre: "À savoir aussi",
            informations: {
                premierExercice: "Pendant votre premier exercice, vous ne payez aucun acompte d'impôt sur les sociétés. Les premiers viendront après votre première clôture.",
                tva2027: "Le régime simplifié de TVA disparaît le 1er janvier 2027. Vos déclarations deviendront trimestrielles si votre chiffre d'affaires ne dépasse pas un million d'euros ; leurs dates n'étaient pas encore publiées au 24 septembre 2026.",
                tvaFranchise: "Vous ne facturez pas la TVA : aucune déclaration à faire. Surveillez vos seuils : 85 000 € de chiffre d'affaires en tout, dont 37 500 € au plus de prestations de services.",
                declarationRevenus: "Au printemps, avec votre déclaration de revenus, vous déclarez aussi votre rémunération de gérant : c'est elle qui ajuste vos cotisations. En 2026, c'était du 9 avril au 21 mai, 28 mai ou 4 juin selon votre département.",
                sansSalaire: "Sans salaire, vous ne payez aucune cotisation. Tant que vous ne vous êtes jamais versé de salaire, l'Urssaf indique qu'aucune déclaration n'est due.",
            },
            agenda: "Ajouter ces dates à mon agenda",
            agendaFichier: "echeances-societe.ics",
            agendaNom: "Échéances de ma société",
            lienDecision: "Préparer la décision de l'associé unique",
            lienVerser: "Combien me verser ce mois-ci",
            recommencer: "Refaire le calendrier",
        },
    },
    en: {
        phases: ["Company", "Taxes", "You"],
        retour: "Back",
        forme: {
            titre: "What is your company?",
            aide: "This calendar covers companies under corporate tax, with a single shareholder.",
            choix: [
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL under corporate tax", detail: "You are the manager, and you pay contributions as self-employed." },
                { cle: "sasu", icone: "user-check-linear", titre: "SASU", detail: "You are the president, treated as an employee." },
                { cle: "ir", icone: "case-minimalistic-linear", titre: "EURL under income tax, or sole trader", detail: "Your profit is taxed in your name." },
            ],
        },
        ir: {
            titre: "This calendar does not cover your situation yet",
            aide: "Under income tax, you have neither corporate tax instalments nor a 2065 return: your deadlines are other dates, which we have not listed yet.",
            points: [
                "The main ones remain the CFE on 15 December and your income tax return in spring.",
                "If you are thinking of switching to corporate tax, first see what it would change for you.",
            ],
            lien: "Switching to corporate tax",
        },
        cloture: {
            titre: "When does your current financial year end?",
            aide: "It is the year-end date in your articles. For most companies, 31 December.",
            trenteEtUn: "31 December {annee}",
            libelle: "Financial year end",
            autre: "Another date",
            date: "Year-end date of the current financial year",
            dateAide: "A future date, within the next two years.",
            erreur: "Choose a future date, within the next two years.",
            suivant: "Continue",
        },
        premier: {
            titre: "Has your company already closed a financial year?",
            aide: "During the first financial year, several deadlines do not exist yet.",
            choix: [
                { cle: "non", icone: "confetti-minimalistic-linear", titre: "No, it is our first year", detail: "The company was set up recently." },
                { cle: "oui", icone: "calendar-mark-linear", titre: "Yes, at least one", detail: "We have already filed a tax return." },
            ],
        },
        creation: {
            titre: "When did your company start trading?",
            aide: "The start date on your Kbis. Your first CFE depends on it.",
            date: "Start of activity",
            suivant: "Continue",
        },
        is: {
            titre: "Was the corporate tax for your last financial year above €3,000?",
            aide: "Below that, you pay all the tax in one go, with the balance. €3,000 of tax is roughly €20,000 of profit.",
            choix: [
                { cle: "plus", icone: "graph-up-linear", titre: "Yes, more than €3,000", detail: "You pay four instalments a year." },
                { cle: "moins", icone: "graph-down-linear", titre: "No, €3,000 or less", detail: "Everything is paid with the balance." },
                { cle: "inconnu", icone: "question-circle-linear", titre: "I do not know", detail: "We will show the instalments, with that caveat." },
            ],
        },
        tva: {
            titre: "How do you declare VAT?",
            aide: "Your accountant or your business account on impots.gouv.fr will tell you.",
            choix: [
                { cle: "franchise", icone: "tag-price-linear", titre: "I do not charge VAT", detail: "Basic exemption: no return." },
                { cle: "simplifie", icone: "calendar-linear", titre: "Two instalments and one return a year", detail: "The simplified regime." },
                { cle: "normal", icone: "calendar-mark-linear", titre: "A return every month", detail: "The normal regime." },
            ],
        },
        social: {
            titreSasu: "Do you pay yourself a salary?",
            aideSasu: "Even a small one: from the first salary, a return goes out every month.",
            choixSasu: [
                { cle: "oui", icone: "wallet-money-linear", titre: "Yes", detail: "I pay myself a president's salary." },
                { cle: "non", icone: "pie-chart-2-linear", titre: "No", detail: "I only take dividends, or nothing." },
            ],
            titreEurl: "When are your contributions collected?",
            aideEurl: "Your schedule on urssaf.fr says so. If you never chose, it is the 5th of the month.",
            choixEurl: [
                { cle: "5", icone: "calendar-linear", titre: "The 5th of each month", detail: "" },
                { cle: "20", icone: "calendar-linear", titre: "The 20th of each month", detail: "" },
                { cle: "trimestre", icone: "calendar-mark-linear", titre: "Every quarter", detail: "5 February, May, August and November." },
            ],
        },
        dividendes: {
            titre: "Do you plan to pay yourself dividends this year?",
            aide: "They add three deadlines to your calendar.",
            choix: [
                { cle: "oui", icone: "pie-chart-2-linear", titre: "Yes", detail: "" },
                { cle: "non", icone: "close-circle-linear", titre: "No", detail: "" },
            ],
        },
        resultat: {
            titre: "Your deadlines",
            entete: "Your next {n} dates",
            enteteUne: "Your next date",
            aucune: "No dated deadline in the next twelve months",
            jours: "in {n} days",
            demain: "tomorrow",
            aujourdhui: "today",
            calculee: "Date calculated from the rule: the 2027 tax calendar has not been published yet.",
            ponctuelles: {
                liasse: {
                    titre: "Tax return for the year ended {cloture}",
                    detail: "Your profit return and its schedules, online. The legal date is {legale}; online, you get fifteen more days.",
                },
                solde: {
                    titre: "Corporate tax balance",
                    detail: "For the year ended {cloture}: the year's tax, minus the instalments already paid. If your instalments exceed the tax, the excess is refunded.",
                },
                approbation: {
                    titre: "Approval of the accounts",
                    detail: "Six months after the year ended {cloture}. You approve the accounts and decide what to do with the profit.",
                },
                depot: {
                    titre: "Filing the accounts with the registry",
                    detail: "One month after your decision, two months online. The date shown assumes a decision taken on the last day allowed.",
                },
                dividendesLimite: {
                    titre: "Last day to pay your dividends",
                    detail: "Nine months after the year ended {cloture}.",
                },
                acompteIs: {
                    titre: "Corporate tax instalment",
                    detail: "A quarter of the tax for your last financial year.",
                    incertain: "Only if that tax was above €3,000.",
                },
                cfe: {
                    titre: "Business property contribution (CFE)",
                    detail: "The balance of your CFE, to be paid online.",
                },
                cfeDeclaration: {
                    titre: "Initial CFE return",
                    detail: "In the year of creation, you pay no CFE, but form 1447-C must be filed before the end of the year.",
                },
                tvaAcompteDecembre: {
                    titre: "December VAT instalment",
                    detail: "40% of last year's VAT, between the 15th and the 24th of December, on the date shown in your business account. You are exempt if that VAT was below €1,000.",
                },
                ifu: {
                    titre: "Return of dividends paid",
                    detail: "Your company declares online the dividends paid the year before, on the single tax form, and sends you a copy.",
                },
                dispense: {
                    titre: "Exemption from the dividend instalment",
                    detail: "For next year's dividends: if your reference tax income is below €50,000, €75,000 as a couple, give your company a sworn statement.",
                },
            },
            recurrentesTitre: "What repeats through the year",
            recurrentes: {
                dsn: "Every 15th of the month: the social return for your salary, and its contributions. Even in a month without salary, the return stays due once the first one has been paid.",
                cotisationsMois: "Every {jour}th of the month: your Urssaf contributions, based on your past income then adjusted once your income is declared.",
                cotisationsTrimestre: "5 February, 5 May, 5 August and 5 November: your Urssaf contributions, based on your past income then adjusted once your income is declared.",
                ca3: "Every month, between the 15th and the 24th: your VAT return. For a company, it is usually the 19th, 20th or 21st, depending on your département: the exact date is in your business account.",
                formulaire2777: "The 15th of the month after each dividend payment: form 2777, which declares and pays the 12.8% income tax instalment and the social levies.",
            },
            informationsTitre: "Also worth knowing",
            informations: {
                premierExercice: "During your first financial year, you pay no corporate tax instalment. The first ones will come after your first year end.",
                tva2027: "The simplified VAT regime disappears on 1 January 2027. Your returns will become quarterly if your turnover does not exceed one million euros; their dates had not been published as of 24 September 2026.",
                tvaFranchise: "You do not charge VAT: no return to file. Watch your thresholds: €85,000 of turnover in all, of which €37,500 at most from services.",
                declarationRevenus: "In spring, with your income tax return, you also declare your pay as a manager: that is what adjusts your contributions. In 2026, it ran from 9 April to 21 May, 28 May or 4 June depending on your département.",
                sansSalaire: "Without a salary, you pay no contributions. As long as you have never paid yourself a salary, the Urssaf says no return is due.",
            },
            agenda: "Add these dates to my calendar",
            agendaFichier: "company-deadlines.ics",
            agendaNom: "My company's deadlines",
            lienDecision: "Prepare the sole shareholder's decision",
            lienVerser: "How much to pay myself this month",
            recommencer: "Build the calendar again",
        },
    },
};

export default function EcheancesSociete({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;
    const jour = String(aujourdhui || new Date().toISOString()).slice(0, 10);
    const annee = Number(jour.slice(0, 4));

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // L'impôt sur le revenu n'est jamais une valeur de l'outil : c'est un
    // écran de refus, où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "eurl" || dossier.forme === "sasu" ? dossier.forme : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [trenteEtUn, setTrenteEtUn] = useState(true);
    const [cloture, setCloture] = useState("");
    const [premier, setPremier] = useState(dossier.premier ?? false);
    const [creation, setCreation] = useState(dossier.creation ?? "");
    const [isReference, setIsReference] = useState("inconnu");
    const [tva, setTva] = useState("franchise");
    const [salaire, setSalaire] = useState(true);
    const [cotisations, setCotisations] = useState("5");
    const [dividendes, setDividendes] = useState(false);

    const { etape, communs, rang, aller, retour, recommencer } = useParcours(
        "forme",
        () => {
            setForme(null);
            setTrenteEtUn(true);
            setCloture("");
            setPremier(false);
            setCreation("");
            setIsReference("inconnu");
            setTva("franchise");
            setSalaire(true);
            setCotisations("5");
            setDividendes(false);
        },
        PHASES,
        reprendre(
            dossier.forme === "ir"
                ? [{ etape: "forme", connu: true }, { etape: "ir" }]
                : [
                      { etape: "forme", connu: formeDossier !== undefined },
                      // La date de clôture ne se déduit pas du dossier : le parcours
                      // s'y arrête, et la date de création reprise attend plus loin.
                      { etape: "cloture", connu: false },
                      { etape: "resultat" },
                  ],
        ),
    );

    useSignalerDossier({ forme, creation }, etape === "resultat");

    const prochaineCloture = trenteEtUn ? `${annee}-12-31` : cloture;
    const clotureValide = useMemo(() => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(prochaineCloture)) return false;
        const limite = `${annee + 2}${jour.slice(4)}`;
        return prochaineCloture >= jour && prochaineCloture <= limite;
    }, [prochaineCloture, annee, jour]);

    const calcul = useMemo(
        () =>
            etape === "resultat"
                ? echeancesSociete({
                      forme,
                      prochaineCloture,
                      premierExercice: premier,
                      dateCreation: premier ? creation : undefined,
                      isReference,
                      tva,
                      salaire,
                      cotisations,
                      dividendes,
                      aujourdhui: jour,
                  })
                : null,
        [etape, forme, prochaineCloture, premier, creation, isReference, tva, salaire, cotisations, dividendes, jour],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const dateDe = (d) => dateLisible(d, lang);
    const lienOutil = (fr, en, libelle, type = "primary") => (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: lang === "fr" ? fr : en } }}>
            <Button type={type} className="press-safe w-full">
                {libelle} →
            </Button>
        </Link>
    );
    const choix = (liste, onChoisir) =>
        liste.map((c) => ({ icone: c.icone, titre: c.titre, detail: c.detail || undefined, onClick: () => onChoisir(c.cle) }));

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={choix(t.forme.choix, (cle) => {
                    if (cle === "ir") {
                        aller("ir");
                        return;
                    }
                    setForme(cle);
                    aller("cloture");
                })}
            />
        );
    }

    if (etape === "ir") {
        return (
            <Ecran key="ir" {...communsEcran} phase={PHASES.ir} rang={rang} titre={t.ir.titre} aide={t.ir.aide}>
                <Liste items={t.ir.points} />
                <div className="mt-5">{lienOutil("passer-a-l-impot-sur-les-societes", "switch-to-corporate-tax", t.ir.lien)}</div>
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "cloture") {
        return (
            <Ecran key="cloture" {...communsEcran} phase={PHASES.cloture} rang={rang} titre={t.cloture.titre} aide={t.cloture.aide}>
                <div className="space-y-5">
                    <Segments
                        libelle={t.cloture.libelle}
                        options={[
                            { cle: "31", libelle: remplir(t.cloture.trenteEtUn, { annee }) },
                            { cle: "autre", libelle: t.cloture.autre },
                        ]}
                        valeur={trenteEtUn ? "31" : "autre"}
                        poser={(v) => setTrenteEtUn(v === "31")}
                    />
                    {!trenteEtUn && (
                        <Champ
                            type="date"
                            libelle={t.cloture.date}
                            aide={t.cloture.dateAide}
                            valeur={cloture}
                            poser={setCloture}
                            erreur={cloture && !clotureValide ? t.cloture.erreur : ""}
                        />
                    )}
                </div>
                <Avancer disabled={!clotureValide} onClick={() => aller("premier")}>
                    {t.cloture.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "premier") {
        return (
            <Question
                key="premier"
                {...communsEcran}
                phase={PHASES.premier}
                rang={rang}
                titre={t.premier.titre}
                aide={t.premier.aide}
                choix={choix(t.premier.choix, (cle) => {
                    setPremier(cle === "non");
                    aller(cle === "non" ? "creation" : "is");
                })}
            />
        );
    }

    if (etape === "creation") {
        const valide = /^\d{4}-\d{2}-\d{2}$/.test(creation) && creation <= jour;
        return (
            <Ecran key="creation" {...communsEcran} phase={PHASES.creation} rang={rang} titre={t.creation.titre} aide={t.creation.aide}>
                <Champ type="date" libelle={t.creation.date} valeur={creation} poser={setCreation} />
                <Avancer disabled={!valide} onClick={() => aller("tva")}>
                    {t.creation.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "is" || etape === "tva") {
        const texte = t[etape];
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                rang={rang}
                titre={texte.titre}
                aide={texte.aide}
                choix={choix(texte.choix, (cle) => {
                    if (etape === "is") {
                        setIsReference(cle);
                        aller("tva");
                    } else {
                        setTva(cle);
                        aller("social");
                    }
                })}
            />
        );
    }

    if (etape === "social") {
        const sasu = forme === "sasu";
        return (
            <Question
                key="social"
                {...communsEcran}
                phase={PHASES.social}
                rang={rang}
                titre={sasu ? t.social.titreSasu : t.social.titreEurl}
                aide={sasu ? t.social.aideSasu : t.social.aideEurl}
                choix={choix(sasu ? t.social.choixSasu : t.social.choixEurl, (cle) => {
                    if (sasu) setSalaire(cle === "oui");
                    else setCotisations(cle);
                    aller("dividendes");
                })}
            />
        );
    }

    if (etape === "dividendes") {
        return (
            <Question
                key="dividendes"
                {...communsEcran}
                phase={PHASES.dividendes}
                rang={rang}
                titre={t.dividendes.titre}
                aide={t.dividendes.aide}
                choix={choix(t.dividendes.choix, (cle) => {
                    setDividendes(cle === "oui");
                    aller("resultat");
                })}
            />
        );
    }

    const { ponctuelles, recurrentes, informations } = calcul;
    const joursAvant = (d) => Math.round((d - new Date(`${jour}T12:00:00Z`)) / 86400000);
    const quand = (d) => {
        const n = joursAvant(d);
        if (n <= 0) return r.aujourdhui;
        if (n === 1) return r.demain;
        return remplir(r.jours, { n });
    };
    const texteDe = (e) => {
        const modele = r.ponctuelles[e.cle];
        const valeurs = {
            cloture: e.cloture ? dateDe(e.cloture) : "",
            legale: e.legale ? dateDe(e.legale) : "",
        };
        return {
            titre: remplir(modele.titre, valeurs),
            detail: [remplir(modele.detail, valeurs), e.incertain ? modele.incertain : ""].filter(Boolean).join(" "),
        };
    };

    const exporter = () => {
        const contenu = construireCalendrier({
            nom: r.agendaNom,
            evenements: ponctuelles.map((e) => {
                const { titre, detail } = texteDe(e);
                return {
                    cle: `${e.cle}-${e.date.toISOString().slice(0, 10)}`,
                    // Le format d'agenda lit la date en heure locale : on la
                    // reconstruit à midi, loin de tout changement de jour.
                    date: new Date(e.date.getUTCFullYear(), e.date.getUTCMonth(), e.date.getUTCDate(), 12),
                    titre,
                    description: detail,
                    rappelJours: 7,
                };
            }),
        });
        telechargerCalendrier(contenu, r.agendaFichier);
    };

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">
                {ponctuelles.length === 0
                    ? r.aucune
                    : ponctuelles.length === 1
                      ? r.enteteUne
                      : remplir(r.entete, { n: ponctuelles.length })}
            </div>

            {ponctuelles.length > 0 && (
                <ol className="mt-5 m-0 p-0 list-none space-y-4">
                    {ponctuelles.map((e) => {
                        const { titre, detail } = texteDe(e);
                        return (
                            <li key={`${e.cle}-${e.date.toISOString()}`} className="flex gap-4">
                                <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border border-primary/30 bg-primary/[0.08]">
                                    <SolarIcon name={ICONES[e.cle]} className="text-lg text-primary" />
                                </span>
                                <div className="min-w-0 pt-1">
                                    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                                        <span className="font-bold text-gray-900 dark:text-white">{dateDe(e.date)}</span>
                                        <span className="text-xs text-gray-500 dark:text-lightBlue/70">{quand(e.date)}</span>
                                    </div>
                                    <div className="font-semibold text-sm dark:text-white mt-0.5">{titre}</div>
                                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-1 mb-0">{detail}</p>
                                    {e.calculee && (
                                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-0">{r.calculee}</p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}

            {ponctuelles.length > 0 && (
                <button
                    type="button"
                    onClick={exporter}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                >
                    <SolarIcon name="calendar-add-linear" className="text-lg" />
                    {r.agenda}
                </button>
            )}

            {recurrentes.length > 0 && (
                <div className="mt-6 rounded-xl border border-gray-200/70 dark:border-white/10 px-4 py-4">
                    <div className="font-semibold dark:text-white">{r.recurrentesTitre}</div>
                    <Liste items={recurrentes.map((e) => remplir(r.recurrentes[e.cle], { jour: e.jour }))} />
                </div>
            )}

            {informations.length > 0 && (
                <div className="mt-6">
                    <div className="font-semibold dark:text-white">{r.informationsTitre}</div>
                    <Liste items={informations.map((cle) => r.informations[cle])} />
                </div>
            )}

            {/* Les liens sont des éléments en ligne : space-y ne les écarte pas,
                le gap d'une colonne flexible, si. */}
            <div className="mt-6 flex flex-col gap-3">
                {lienOutil("decision-associe-unique", "sole-shareholder-decision", r.lienDecision, "secondary")}
                {lienOutil("combien-me-verser-ce-mois-ci", "how-much-to-pay-yourself-this-month", r.lienVerser, "secondary")}
            </div>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
